import { useRef, useState } from 'react';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { ComponentProps } from '@components/strapi/DynamicZoneComponent';
import { NewsletterSectionDTO } from '@lib/strapi/dtos/components/sections/newsletterSection.dto';
import { Prose } from '../shared-ui';
import Turnstile from '@components/utils/turnstile';
import * as gtag from '@lib/utilities/analytics';

/**
 * Mailchimp signup — relocated here from the footer (D15) so the footer is not
 * stacking competing CTAs. Posts to /api/subscribe, which verifies Turnstile
 * and adds the address as PENDING: Mailchimp sends the confirmation e-mail
 * (double opt-in, owner 2026-09-25 — same as nutrihabeats), so the success
 * copy says «check your inbox», and a one-line privacy note sits under the field.
 */
const NewsletterSectionImplementation = ({ section }: ComponentProps<NewsletterSectionDTO>) => {
  const { t } = useTranslation('main');
  const [state, setState] = useState<'idle' | 'ok' | 'exists' | 'fail' | 'verify'>('idle');
  // after a submit without a token the widget is re-rendered VISIBLE (checkbox) with a hint
  const [widgetAppearance, setWidgetAppearance] = useState<'interaction-only' | 'always'>('interaction-only');
  // the token is read through a ref so a submit can WAIT for it: on a one-field
  // form a visitor pastes and hits Enter before the interaction-only widget has
  // issued its token (~1s after load). Tokens are single-use, so after a failed
  // request the widget is remounted (key) to get a fresh one for the retry.
  const tokenRef = useRef<string | null>(null);
  const [widgetKey, setWidgetKey] = useState(0);
  const setTurnstileToken = (token: string | null) => { tokenRef.current = token; };
  const awaitToken = async (): Promise<string | null> => {
    if (!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) return null;
    for (let i = 0; i < 60 && !tokenRef.current; i++) await new Promise((r) => setTimeout(r, 100));
    return tokenRef.current;
  };
  const needsVerification = () => { setWidgetAppearance('always'); setWidgetKey((k) => k + 1); setState('verify'); };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const email = new FormData(e.currentTarget).get('email');
    const turnstileToken = await awaitToken();
    // no token after 6s = Cloudflare wants a click the visitor has not made (or the
    // invisible widget never showed it): show the checkbox and ask, do not post
    if (process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && !turnstileToken) return needsVerification();
    const res = await fetch('/api/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, turnstileToken }),
    });
    if (!res.ok) { tokenRef.current = null; if (res.status === 403) return needsVerification(); setWidgetKey((k) => k + 1); }
    // 409 = Mailchimp «Member Exists» — already on the list is not an error
    // for the subscriber, so it must not read as one (owner, 2026-09-08)
    if (res.ok) gtag.event('newsletter_submit', { event_category: 'conversion' });
    setState(res.ok ? 'ok' : res.status === 409 ? 'exists' : 'fail');
  };

  return (
    <section className="my-14 rounded-2xl bg-violet-050 p-8 md:my-20">
      {section?.heading ? <h2 className="text-xl font-extrabold text-ink">{section.heading}</h2> : null}
      {/* base size, not text-sm — 14px serif was the worst of both worlds */}
      <Prose content={section?.intro} className="mt-2 text-base text-muted" />
      {state === 'ok' ? (
        <p className="mt-4 font-bold text-violet-700">{t('newsletter.success')}</p>
      ) : state === 'exists' ? (
        <p className="mt-4 font-bold text-violet-700">{t('newsletter.exists')}</p>
      ) : (
        <form onSubmit={onSubmit} className="mt-4 flex max-w-md flex-wrap gap-3">
          <input name="email" type="email" required placeholder="you@example.com"
            className="min-w-0 flex-1 basis-[14rem] rounded-full border-[1.5px] border-rule bg-white px-5 py-3 text-ink focus:border-violet-700 focus:outline-none" />
          <button type="submit"
            className="rounded-full bg-violet-700 px-6 py-3 font-extrabold text-white transition-colors hover:bg-violet-800">
            {t('newsletter.button')}
          </button>
          <Turnstile key={widgetKey} appearance={widgetAppearance} onToken={setTurnstileToken} className="w-full" />
          <p className="w-full text-xs text-muted">
            {t('newsletter.consentBefore')}
            <Link href="/politiki-aporritou" className="font-bold text-violet-700 underline underline-offset-2">{t('newsletter.privacyLink')}</Link>
            {t('newsletter.consentAfter')}
          </p>
          {state === 'verify' ? <p className="w-full text-sm font-bold text-violet-700">{t('newsletter.verify')}</p> : null}
          {state === 'fail' ? <p className="w-full text-sm font-bold text-error">{t('newsletter.failure')}</p> : null}
        </form>
      )}
    </section>
  );
};

export default NewsletterSectionImplementation;
